/**
 * ProjectRax — TierGate Component
 *
 * Wraps any feature that requires a specific plan tier.
 * If the org's plan meets the requirement, renders children normally.
 * If not, shows an upgrade prompt.
 *
 * Usage:
 *   <TierGate feature="anomaly_detection_project">
 *     <AnomaliesPage />
 *   </TierGate>
 *
 *   // Inline check (no wrapper UI):
 *   const { hasTier } = useTierCheck('disbursement_pack');
 *   if (hasTier) showButton();
 */
import { useState, useEffect, createContext, useContext } from 'react';
import tierService from '../services/tierService';

// ─── Context: org plan loaded once, shared everywhere ───────

const TierContext = createContext({
  plan: 'free',
  features: {},
  loading: true,
  reload: () => {},
});

export function TierProvider({ children }) {
  const [state, setState] = useState({ plan: 'free', features: {}, loading: true });

  const load = async () => {
    try {
      const res = await tierService.getFeatures();
      const d = res.data.data;
      const featureMap = {};
      (d.available || []).forEach((f) => { featureMap[f.featureId] = { ...f, accessible: true }; });
      (d.locked || []).forEach((f) => { featureMap[f.featureId] = { ...f, accessible: false }; });
      setState({ plan: d.currentPlan, features: featureMap, loading: false });
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <TierContext.Provider value={{ ...state, reload: load }}>
      {children}
    </TierContext.Provider>
  );
}

export function useTier() {
  return useContext(TierContext);
}

/**
 * Hook: check if a feature is accessible
 * @param {string} featureId
 * @returns {{ hasTier: boolean, feature: object|null, loading: boolean }}
 */
export function useTierCheck(featureId) {
  const { features, loading } = useTier();
  const feature = features[featureId] || null;
  return {
    hasTier: feature?.accessible ?? false,
    feature,
    loading,
  };
}

// ─── Plan badge colors ──────────────────────────────────────

const PLAN_COLORS = {
  free: { bg: '#e2e8f0', text: '#64748b', border: '#cbd5e1' },
  professional: { bg: '#dbeafe', text: '#2563eb', border: '#93c5fd' },
  premium: { bg: '#ede9fe', text: '#7c3aed', border: '#c4b5fd' },
  enterprise: { bg: '#fef3c7', text: '#d97706', border: '#fcd34d' },
};

const PLAN_ICONS = { free: '🏗️', professional: '⚡', premium: '👑', enterprise: '🏢' };

// ─── Gate Component ─────────────────────────────────────────

export default function TierGate({ feature, children, fallback = null }) {
  const { features, plan, loading } = useTier();

  if (loading) {
    return fallback || (
      <div className="d-flex justify-content-center p-4">
        <div className="spinner-border spinner-border-sm text-secondary" />
      </div>
    );
  }

  const feat = features[feature];

  // Feature not in map or accessible → render children
  if (!feat || feat.accessible) {
    return children;
  }

  // Locked → show upgrade prompt
  const requiredPlan = feat.requiredPlan || feat.minPlan;
  const planColor = PLAN_COLORS[requiredPlan] || PLAN_COLORS.professional;

  return (
    <div className="d-flex align-items-center justify-content-center" style={{ minHeight: 300 }}>
      <div className="text-center p-4" style={{ maxWidth: 480 }}>
        {/* Lock icon */}
        <div
          className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
          style={{ width: 64, height: 64, background: planColor.bg, border: `2px solid ${planColor.border}` }}
        >
          <i className="bi bi-lock-fill" style={{ fontSize: 24, color: planColor.text }} />
        </div>

        {/* Feature name */}
        <h5 className="mb-2" style={{ color: '#1e293b', fontWeight: 700 }}>
          {feat.name}
        </h5>

        {/* Description */}
        <p className="text-muted mb-3" style={{ fontSize: 14, lineHeight: 1.6 }}>
          {feat.description}
        </p>

        {/* Plan badge */}
        <div className="mb-3">
          <span
            className="badge"
            style={{
              background: planColor.bg,
              color: planColor.text,
              border: `1px solid ${planColor.border}`,
              fontSize: 12,
              padding: '4px 12px',
            }}
          >
            {PLAN_ICONS[requiredPlan]} Requires {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)} Plan
          </span>
        </div>

        {/* Current plan info */}
        <p className="text-muted mb-3" style={{ fontSize: 13 }}>
          Your current plan: <strong>{plan.charAt(0).toUpperCase() + plan.slice(1)}</strong>
        </p>

        {/* Upgrade button */}
        <a href="/plan" className="btn btn-sm" style={{ background: planColor.text, color: '#fff', borderRadius: 8, padding: '8px 24px' }}>
          <i className="bi bi-arrow-up-circle me-1" />
          Upgrade to {requiredPlan.charAt(0).toUpperCase() + requiredPlan.slice(1)}
        </a>
      </div>
    </div>
  );
}

/**
 * Inline tier badge — shows a small lock icon + plan name next to a nav item
 * Usage: <TierBadge feature="anomaly_detection_project" />
 */
export function TierBadge({ feature }) {
  const { features } = useTier();
  const feat = features[feature];

  if (!feat || feat.accessible) return null;

  const requiredPlan = feat.requiredPlan || feat.minPlan;
  const color = PLAN_COLORS[requiredPlan] || PLAN_COLORS.professional;

  return (
    <span
      className="badge ms-1"
      style={{
        background: color.bg,
        color: color.text,
        fontSize: 9,
        padding: '2px 6px',
        fontWeight: 600,
        verticalAlign: 'middle',
      }}
      title={`Requires ${requiredPlan} plan`}
    >
      {PLAN_ICONS[requiredPlan]}
    </span>
  );
}
